// Vista de MESA DE AYUDA / SOPORTE (pestaña de Configuración → Ayuda).
//
// Disponible para CUALQUIER usuario autenticado. Permite:
//   - Ver los temas más comunes como referencia (se eligen con un clic).
//   - Describir la ayuda PUNTUAL que se necesita (solicitud de soporte).
//   - Enviar una recomendación o idea de funcionalidad adicional (feedback).
// Cada caso se guarda en BD con un número secuencial y se envía por correo al
// administrador. Al crear, se muestra el número de caso asignado.

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useApi } from '../api/ApiContext.js';
import { useAuth } from '../auth/AuthContext.js';
import type { CreateSupportTicket, SupportTicket, SupportTicketType } from '../api/endpoints.js';
import { AlertMessage, Card, Icon, PageHeader, Spinner } from '../ui/components.js';
import { useAsync } from '../ui/useAsync.js';

export function SupportView() {
  const { t } = useTranslation();
  const api = useApi();
  const { permissions } = useAuth();
  const topics = useAsync(() => api.support.topics(), []);
  const history = useAsync(() => api.support.list(), []);

  // Nombre y correo de contacto PRELLENADOS con los datos de la cuenta; el
  // usuario puede editarlos. Vienen de /me/permissions (AuthContext).
  const accountName = permissions?.displayName ?? '';
  const accountEmail = permissions?.email ?? '';

  const [type, setType] = useState<SupportTicketType>('support');
  const [category, setCategory] = useState<string>('other');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [contactName, setContactName] = useState(accountName);
  const [contactEmail, setContactEmail] = useState(accountEmail);

  // Los permisos cargan de forma asíncrona: cuando lleguen, prellena nombre/
  // correo SOLO si el usuario aún no ha escrito nada (no pisa su edición).
  useEffect(() => {
    if (accountName) setContactName((v) => (v === '' ? accountName : v));
    if (accountEmail) setContactEmail((v) => (v === '' ? accountEmail : v));
    // Depende solo de la llegada async de permisos (`accountName`/`accountEmail`);
    // los setters de estado son estables y no deben ir en el arreglo de deps.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountName, accountEmail]);

  const [submitting, setSubmitting] = useState(false);
  const [created, setCreated] = useState<SupportTicket | null>(null);
  const [emailSent, setEmailSent] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const topicItems = topics.data?.items ?? [];
  const selectedTopic = useMemo(
    () => topicItems.find((topic) => topic.key === category) ?? null,
    [topicItems, category],
  );

  const canSubmit = subject.trim().length > 0 && message.trim().length > 0 && !submitting;

  function resetForm() {
    setSubject('');
    setMessage('');
    // Vuelve a los datos de la cuenta (no se vacían: vienen prellenados).
    setContactName(accountName);
    setContactEmail(accountEmail);
    setCategory('other');
    setType('support');
  }

  async function submit() {
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    setCreated(null);
    try {
      const body: CreateSupportTicket = {
        type,
        category,
        subject: subject.trim(),
        message: message.trim(),
      };
      if (contactName.trim()) body.contact_name = contactName.trim();
      if (contactEmail.trim()) body.contact_email = contactEmail.trim();

      const res = await api.support.create(body);
      setCreated(res.ticket);
      setEmailSent(res.email_sent);
      resetForm();
      history.reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('support.errSend'));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="flex flex-col">
      <PageHeader
        title={t('support.pageTitle')}
        subtitle={t('support.pageSubtitle')}
        back
      />

      <div className="flex flex-col px-margin-mobile py-space-md gap-space-md">
        {/* Confirmación con número de caso. */}
        {created && (
          <div className="surface-card border border-goldmine-mint/40 bg-goldmine-mint/5 flex items-start gap-space-sm">
            <Icon name="check_circle" className="text-goldmine-mint text-[22px] mt-0.5" />
            <div className="flex flex-col gap-0.5">
              <span className="font-headline-sm text-headline-sm text-on-surface">
                {t('support.caseRegistered', { number: created.case_number })}
              </span>
              <span className="font-body-sm text-body-sm text-on-surface-variant">
                {t('support.caseSavedPre')}
                {created.type === 'feedback' ? t('support.caseRecommendation') : t('support.caseRequest')}
                {t('support.caseSavedMid')}
                <strong>#{created.case_number}</strong>.{' '}
                {emailSent ? t('support.emailSentYes') : t('support.emailSentNo')}
              </span>
            </div>
          </div>
        )}

        {/* Tipo de caso: ayuda o recomendación. */}
        <Card title={t('support.cardTitle')} icon="support_agent">
          <div className="grid grid-cols-2 gap-space-xs mb-space-md">
            <TypeTab
              active={type === 'support'}
              icon="help"
              label={t('support.needHelp')}
              onClick={() => setType('support')}
            />
            <TypeTab
              active={type === 'feedback'}
              icon="lightbulb"
              label={t('support.haveIdea')}
              onClick={() => setType('feedback')}
            />
          </div>

          {/* Temas comunes (solo para solicitudes de ayuda). */}
          {type === 'support' && (
            <div className="mb-space-md">
              <p className="section-label text-on-surface-variant mb-space-xs">
                {t('support.commonTopics')}
              </p>
              {topics.loading && <Spinner label={t('support.loadingTopics')} />}
              {topics.error && (
                <p className="text-error font-body-sm text-body-sm">{String(topics.error)}</p>
              )}
              <div className="grid grid-cols-1 gap-space-xs">
                {topicItems.map((topic) => {
                  const checked = category === topic.key;
                  return (
                    <label
                      key={topic.key}
                      className={`flex items-start gap-space-xs px-space-sm py-space-xs rounded-lg border cursor-pointer transition-colors ${
                        checked
                          ? 'border-primary-container bg-primary-container/10 text-primary'
                          : 'border-border-subtle text-on-surface-variant hover:border-outline'
                      }`}
                    >
                      <input
                        type="radio"
                        name="support-topic"
                        className="accent-primary mt-0.5"
                        checked={checked}
                        onChange={() => setCategory(topic.key)}
                      />
                      <span className="flex flex-col">
                        <span className="font-body-md text-body-md leading-tight">{topic.label}</span>
                        <span className="font-body-sm text-body-sm text-on-surface-variant leading-tight">
                          {topic.description}
                        </span>
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Formulario del caso. */}
          <div className="flex flex-col gap-space-sm">
            <label className="flex flex-col gap-1">
              <span className="section-label text-on-surface-variant">
                {type === 'feedback' ? t('support.subjectFeedback') : t('support.subjectSupport')}
              </span>
              <input
                className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                placeholder={
                  type === 'feedback'
                    ? t('support.subjectFeedbackPlaceholder')
                    : selectedTopic && selectedTopic.key !== 'other'
                      ? t('support.subjectTopicPlaceholder', { topic: selectedTopic.label })
                      : t('support.subjectDefaultPlaceholder')
                }
                maxLength={160}
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="section-label text-on-surface-variant">
                {type === 'feedback' ? t('support.messageFeedback') : t('support.messageSupport')}
              </span>
              <textarea
                className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm py-space-xs min-h-[120px] resize-y"
                placeholder={
                  type === 'feedback'
                    ? t('support.messageFeedbackPlaceholder')
                    : t('support.messageSupportPlaceholder')
                }
                maxLength={4000}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
              />
            </label>

            <div className="grid grid-cols-2 gap-space-xs">
              <label className="flex flex-col gap-1">
                <span className="section-label text-on-surface-variant">{t('support.yourName')}</span>
                <input
                  className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                  placeholder={t('support.yourNamePlaceholder')}
                  maxLength={160}
                  value={contactName}
                  onChange={(e) => setContactName(e.target.value)}
                />
              </label>
              <label className="flex flex-col gap-1">
                <span className="section-label text-on-surface-variant">{t('support.contactEmail')}</span>
                <input
                  type="email"
                  className="field-input bg-surface-deck rounded border border-border-subtle px-space-sm h-10"
                  placeholder={t('support.contactEmailPlaceholder')}
                  maxLength={160}
                  value={contactEmail}
                  onChange={(e) => setContactEmail(e.target.value)}
                />
              </label>
            </div>

            <AlertMessage message={error} className="mt-space-xs" />

            <button
              className="btn-primary w-full mt-space-xs"
              type="button"
              disabled={!canSubmit}
              onClick={() => void submit()}
            >
              <Icon name="send" className="text-[18px]" />
              {submitting ? t('support.sending') : type === 'feedback' ? t('support.sendFeedback') : t('support.sendSupport')}
            </button>
          </div>
        </Card>

        {/* Historial de casos del usuario / tenant. */}
        <Card title={t('support.myCases')} icon="inbox" collapsible defaultOpen={false}>
          {history.loading && <Spinner label={t('support.loadingCases')} />}
          {history.error && (
            <p className="text-error font-body-sm text-body-sm">{String(history.error)}</p>
          )}
          {!history.loading && (history.data?.items.length ?? 0) === 0 && (
            <p className="font-body-sm text-body-sm text-on-surface-variant italic">
              {t('support.noCases')}
            </p>
          )}
          <div className="flex flex-col gap-space-xs">
            {(history.data?.items ?? []).map((ticket) => (
              <TicketRow key={ticket.id} ticket={ticket} />
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}

/** Pestaña para elegir el tipo de caso (ayuda o idea). */
function TypeTab({
  active,
  icon,
  label,
  onClick,
}: {
  active: boolean;
  icon: string;
  label: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex items-center justify-center gap-space-xs h-11 rounded-lg border transition-colors ${
        active
          ? 'border-primary-container bg-primary-container/10 text-primary'
          : 'border-border-subtle text-on-surface-variant hover:border-outline'
      }`}
    >
      <Icon name={icon} className="text-[18px]" />
      <span className="font-body-md text-body-md">{label}</span>
    </button>
  );
}

/** Etiqueta i18n del estado de un ticket. */
function statusLabel(t: TFunction, status: SupportTicket['status']): string {
  return t(`support.status.${status}`, { defaultValue: status });
}

/** Una fila del historial de casos. */
function TicketRow({ ticket }: { ticket: SupportTicket }) {
  const { t } = useTranslation();
  const date = new Date(ticket.created_at);
  const dateLabel = Number.isNaN(date.getTime())
    ? ticket.created_at
    : date.toLocaleDateString();
  return (
    <div className="rounded-lg border border-border-subtle p-space-sm flex items-start gap-space-sm">
      <span className="shrink-0 inline-flex items-center justify-center min-w-[44px] h-6 px-2 rounded-full bg-surface-rack text-vacuum-glow font-badge-micro text-badge-micro">
        #{ticket.case_number}
      </span>
      <div className="flex-1 min-w-0 flex flex-col gap-0.5">
        <span className="font-body-md text-body-md text-on-surface truncate">{ticket.subject}</span>
        <span className="font-body-sm text-body-sm text-on-surface-variant">
          {ticket.type === 'feedback' ? t('support.typeRecommendation') : t('support.typeHelp')} · {statusLabel(t, ticket.status)} · {dateLabel}
        </span>
      </div>
    </div>
  );
}
